import React, { useState } from 'react';
import { Check, Lock, Unlock, Pencil, Star, Trash2, Upload } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const VideoGrid = ({
  videos,
  selectedBg,
  selectedImageBg,
  hoveredVideo,
  defaultVideoId,
  uploadingVideo,
  onBgSelect,
  onHoverVideo,
  onVideoRename,
  onVideoToggleLock,
  onVideoDelete,
  onSetDefaultVideo,
  onVideoUpload,
  testIdPrefix = ''
}) => {
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2 max-h-52 overflow-y-auto">
        {videos.map((bg, index) => (
          <div
            key={bg.id}
            className={`relative rounded-lg overflow-hidden aspect-video border-2 transition-all group ${
              selectedBg === index && selectedImageBg === null
                ? 'border-purple-400 ring-2 ring-purple-400/30'
                : 'border-transparent hover:border-white/30'
            }`}
          >
            <button
              onClick={() => onBgSelect(index)}
              onMouseEnter={() => onHoverVideo(bg.id)}
              onMouseLeave={() => onHoverVideo(null)}
              className="w-full h-full"
              data-testid={`${testIdPrefix}video-bg-${bg.id}`}
            >
              <video
                src={bg.url?.startsWith('http') ? bg.url : `${API}${bg.url}`}
                autoPlay={hoveredVideo === bg.id}
                muted loop playsInline preload="metadata"
                className="w-full h-full object-cover"
                ref={(el) => {
                  if (el) {
                    if (hoveredVideo === bg.id) { el.play().catch(() => {}); }
                    else { el.pause(); el.currentTime = 0; }
                  }
                }}
              />
            </button>
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-1 flex items-center gap-1">
              {editingId === bg.id ? (
                <input
                  autoFocus
                  className="text-white text-xs bg-white/20 rounded px-1 py-0.5 w-full outline-none"
                  value={editingName}
                  onChange={(e) => setEditingName(e.target.value)}
                  onBlur={() => {
                    if (editingName.trim() && editingName !== bg.name) onVideoRename(bg.id, editingName.trim());
                    setEditingId(null);
                  }}
                  onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') setEditingId(null); }}
                  data-testid={`${testIdPrefix}video-rename-input-${bg.id}`}
                />
              ) : (
                <p className="text-white text-xs truncate flex-1">{bg.name}</p>
              )}
              {bg.locked && <Lock className="w-3 h-3 text-amber-400 shrink-0" />}
            </div>
            {selectedBg === index && selectedImageBg === null && (
              <div className="absolute top-1 right-1 w-5 h-5 bg-purple-500 rounded-full flex items-center justify-center">
                <Check className="w-3 h-3 text-white" />
              </div>
            )}
            {defaultVideoId === bg.id && !(selectedBg === index && selectedImageBg === null) && (
              <div className="absolute top-1 right-1 w-5 h-5 bg-amber-500 rounded-full flex items-center justify-center">
                <Star className="w-3 h-3 text-white fill-white" />
              </div>
            )}
            <div className="absolute top-1 left-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <button onClick={(e) => { e.stopPropagation(); onSetDefaultVideo(bg.id); }}
                className={`w-6 h-6 rounded-full flex items-center justify-center transition-colors ${defaultVideoId === bg.id ? 'bg-amber-500/80' : 'bg-black/60 hover:bg-black/80'}`}
                title="Fond par défaut" data-testid={`${testIdPrefix}video-default-btn-${bg.id}`}>
                <Star className={`w-3 h-3 ${defaultVideoId === bg.id ? 'text-white fill-white' : 'text-white/60'}`} />
              </button>
              <button onClick={(e) => { e.stopPropagation(); setEditingId(bg.id); setEditingName(bg.name); }}
                className="w-6 h-6 rounded-full bg-black/60 flex items-center justify-center hover:bg-black/80 transition-colors"
                title="Renommer" data-testid={`${testIdPrefix}video-rename-btn-${bg.id}`}>
                <Pencil className="w-3 h-3 text-white" />
              </button>
              <button onClick={(e) => { e.stopPropagation(); onVideoToggleLock(bg); }}
                className="w-6 h-6 rounded-full bg-black/60 flex items-center justify-center hover:bg-black/80 transition-colors"
                title={bg.locked ? 'Déverrouiller' : 'Verrouiller'} data-testid={`${testIdPrefix}video-lock-btn-${bg.id}`}>
                {bg.locked ? <Lock className="w-3 h-3 text-amber-400" /> : <Unlock className="w-3 h-3 text-white/60" />}
              </button>
              {!bg.locked && (
                <button onClick={(e) => { e.stopPropagation(); onVideoDelete(bg); }}
                  className="w-6 h-6 rounded-full bg-red-500/60 flex items-center justify-center hover:bg-red-500/80 transition-colors"
                  title="Supprimer" data-testid={`${testIdPrefix}video-delete-btn-${bg.id}`}>
                  <Trash2 className="w-3 h-3 text-white" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
      <div className="p-3 bg-white/5 rounded-xl">
        <p className="text-white/60 text-xs uppercase tracking-wider mb-2">Ajouter une vidéo</p>
        <label className="cursor-pointer">
          <input type="file" accept=".mp4,.webm,.mov" className="hidden"
            onChange={(e) => { const file = e.target.files[0]; if (file) onVideoUpload(file); }}
            disabled={uploadingVideo} data-testid={`${testIdPrefix}video-upload-input`} />
          <div className={`w-full py-3 rounded-lg border-2 border-dashed transition-all flex items-center justify-center gap-2 ${
            uploadingVideo ? 'border-white/20' : 'border-white/30 hover:border-purple-400'}`}>
            {uploadingVideo ? (
              <div className="w-4 h-4 border-2 border-purple-400 border-t-transparent rounded-full animate-spin" />
            ) : (<><Upload className="w-4 h-4 text-white/50" /><span className="text-white/70 text-xs">Cliquez pour ajouter une vidéo</span></>)}
          </div>
        </label>
      </div>
    </div>
  );
};

export default VideoGrid;
